import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Flex, List, Modal, Tag, Typography } from 'antd'
import Button from '../../common/Button'
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'

const EditDtcsMappingHeader = ({ dtcs, vehicleSid }) => {
  const intl = useIntl()
  const timeoutRef = useRef()
  const dtcContainer = useRef(null)
  const dtcWrapper = useRef(null)
  const dtcsList = useRef(null)
  const [overflow, setOverflow] = useState(false)
  const [sortOrder, setSortOrder] = useState(null)
  const [showDtcDetails, setShowDtcDetails] = useState(false)

  // Display ellipsis next to the selected DTCs on resize
  useEffect(() => {
    if (!dtcs || !dtcContainer.current || !dtcWrapper.current) return

    const elementContainer = dtcContainer.current
    const elementWrapper = dtcWrapper.current

    const observer = new ResizeObserver(([entry]) => {
      clearTimeout(timeoutRef.current)

      elementWrapper.classList.remove('hide-overflow')

      timeoutRef.current = setTimeout(() => {
        const hasOverflow =
          elementContainer.scrollWidth > elementContainer.clientWidth
        if (hasOverflow) {
          elementWrapper.classList.add('hide-overflow')
        } else {
          elementWrapper.classList.remove('hide-overflow')
        }
        setOverflow(hasOverflow)
      }, 100)
    })

    observer.observe(elementContainer)

    return () => {
      clearTimeout(timeoutRef.current)
      observer.disconnect()
    }
  }, [dtcs, dtcContainer.current])

  // Sort dtcs
  const sortedData = useMemo(() => {
    if (!sortOrder) return dtcs

    return [...dtcs].sort((a, b) => {
      return sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    })
  }, [dtcs, sortOrder])

  const toggleSort = () => {
    setSortOrder((prev) => {
      switch (prev) {
        case null:
          return 'asc'
        case 'asc':
          return 'desc'
        default:
          return null
      }
    })
  }

  const getDTCDetails = useMemo(() => {
    const length = sortedData?.length

    return (
      <Flex vertical>
        <Flex
          align="center"
          gap={8}
          className="sorting-column-header scrollable-list"
          onClick={toggleSort}
        >
          <Typography.Text>{intl.formatMessage({ id: 'dtcs.listing.selectedDtc' })}</Typography.Text>
          <Flex vertical gap={0} className="sort-icon">
            <CaretUpOutlined className={sortOrder === 'asc' ? 'active' : ''} />
            <CaretDownOutlined
              className={sortOrder === 'desc' ? 'active' : ''}
            />
          </Flex>
        </Flex>

        <List
          bordered
          dataSource={sortedData}
          className="dtcs"
          ref={dtcsList}
          renderItem={(dtc, index) => (
            <List.Item key={`dtc-${index}`} className="dtc-item">
              <Flex gap={0} vertical>
                <Typography.Text className="dtc-name">
                  {dtc.name}
                </Typography.Text>
                {dtc.acronym ? (
                  <Typography.Text className="dtc-acronym">
                    {' '}
                    - {dtc.acronym}
                  </Typography.Text>
                ) : (
                  ''
                )}
                <Typography.Text className="dtc-description">
                  {dtc.description}
                </Typography.Text>
              </Flex>
            </List.Item>
          )}
        />
      </Flex>
    )
  }, [sortedData])

  const showSelectedDTCsModal = useMemo(() => {
    const title = intl.formatMessage({ id: 'dtcs.connectorMapping.selectedDtcCount' }, { count: dtcs.length })
    const content = getDTCDetails

    return (
      <Modal
        open={showDtcDetails}
        title={title}
        width="60%"
        className="edit-dtc-mapping-modal"
        centered={true}
        footer={null}
        closable={true}
        onCancel={() => setShowDtcDetails(false)}
      >
        {content}
      </Modal>
    )
  }, [sortedData, showDtcDetails])

  useEffect(() => {
    if (!dtcsList.current || !sortedData) return

    dtcsList.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }, [sortOrder, sortedData])

  return (
    <>
      <Flex align="center" gap={16} className="dtcs-list">
        <Flex
          ref={dtcContainer}
          align="center"
          wrap="nowrap"
          gap={5}
          className="dtcs-container"
        >
          <div ref={dtcWrapper} size={0} className="dtcs-wrapper">
            {dtcs.map((dtc) => (
              <Tag key={dtc.name}>{dtc.name}</Tag>
            ))}
          </div>

          {overflow && (
            <Typography.Paragraph className="ellipsis">
              ...
            </Typography.Paragraph>
          )}
        </Flex>

        <Button onClick={() => setShowDtcDetails(true)}>{intl.formatMessage({ id: 'dtcs.listing.details' })}</Button>
      </Flex>

      {showDtcDetails && showSelectedDTCsModal}
    </>
  )
}

EditDtcsMappingHeader.propTypes = {
  dtcs: PropTypes.array.isRequired,
  vehicleSid: PropTypes.string.isRequired,
}

export default EditDtcsMappingHeader
